import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { GoogleAuthService } from 'ng-gapi';
import { reset } from '../actions/meta.actions';
import { setRaas, setPermissions } from '../actions/consultant.actions';
import { AppConfig } from '../helpers/app.config';
import { CrossStorage } from '../helpers/cross-storage';
import { UtilsService } from './utils.service';
import { RaasLogin } from '../models/raas-login.model';
import GoogleUser = gapi.auth2.GoogleUser;
import { LoadingService } from './loading.service';
import { OriginalPermission } from '../models/consultant.model';
import { LoginGoogleComponent } from '../components/consultant/login-google/login-google.component';
import jwtDecode from 'jwt-decode';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {

  private googleLogin: LoginGoogleComponent;

  constructor(
    private httpClient: HttpClient,
    private store: Store,
    private router: Router,
    private ngZone: NgZone,
    private googleAuthService: GoogleAuthService,
    private utilsService: UtilsService,
    public loadingService: LoadingService
  ) { }

  // hacemos login contra google
  signinWithGoogle() {
    this.googleLogin = new LoginGoogleComponent();

    if (!this.onLoginProcess()) {
      this.googleLogin.init().then(() => {
        if (this.onLoginProcess()) {
          this.signInSuccessHandler();
        }
      });
    } else if (this.onLoginProcess()) {
      this.signInSuccessHandler();
    }
  }

  private onLoginProcess(): boolean {
    return sessionStorage.getItem('googleToken') != null &&
      sessionStorage.getItem('basicProfileMail') != null &&
      sessionStorage.getItem('hostedDomain') != null;
  }

  // gestion login de google correcto
  private async signInSuccessHandler() {

    const basicProfileMail = sessionStorage.getItem('basicProfileMail');
    const userId = jwtDecode(basicProfileMail);

    const raasLoginData: RaasLogin = {
      gToken: sessionStorage.getItem('googleToken'),
      userId: userId['email'],
      hostedDomain: sessionStorage.getItem('hostedDomain')
    };

    this.raasLogin(raasLoginData).subscribe(async res => {

      const clientAndDirectoryData = await this.httpClient.post<any>(`${AppConfig.settings.API}/consultant/login`, raasLoginData).toPromise();
      res.client = clientAndDirectoryData.client;
      res.googleDirectory = clientAndDirectoryData.googleDirectory;

      await this.setRandstadData(res);
      sessionStorage.removeItem('googleToken');
      sessionStorage.removeItem('basicProfileMail');
      sessionStorage.removeItem('hostedDomain');
    }, error => {
      sessionStorage.removeItem('googleToken');
      sessionStorage.removeItem('basicProfileMail');
      sessionStorage.removeItem('hostedDomain');
      this.store.dispatch(reset());
      location.href = AppConfig.settings.LOGOUT_FBI;
    });
  }

  // control de error en login
  private signInErrorHandler(err) {
    this.loadingService.stopLoading();
    console.warn(err);
  }

  // requerimos el storage de FBI
  requestCrossStorage(): Promise<any[]> {
    const urlFBIHome = AppConfig.settings.LOGOUT_FBI.replace('/login', '');
    const crossStorage = new CrossStorage(urlFBIHome, '/authDomain.html');

    const apiTokenPromise = new Promise((resolve, reject) => {
      crossStorage.requestValue('apiToken', (key, value) => resolve(value));
    });
    const googleDirectoryPromise = new Promise((resolve, reject) => {
      crossStorage.requestValue('googleDirectory', (key, value) => resolve(value));
    });
    const googleAuthPromise = new Promise((resolve, reject) => {
      crossStorage.requestValue('googleAuth', (key, value) => resolve(value));
    });

    return Promise.all([apiTokenPromise, googleDirectoryPromise, googleAuthPromise]);
  }

  signInWithCrossStorage() {
    this.requestCrossStorage().then(fbiData => {
      if (fbiData[0] !== null && fbiData[1] !== null && fbiData[2] !== null) {
        this.crossStorageLogin(fbiData).subscribe(res => {
          this.setRandstadData(res);
        });
      }
    });
  }

  async setRandstadData(data) {
    const googleDirectory = JSON.parse(this.utilsService.b64DecodeUnicode(data.googleDirectory));

    const client = JSON.parse(this.utilsService.b64DecodeUnicode(data.client));
    client.defaultLanguage = this.utilsService.getLanguageFromLanguageId(client.id);

    // actualizamos el reducer consultor
    this.store.dispatch(setRaas({
      token: data.access_token,
      client: client,
      googleDirectory: googleDirectory,
      raas: jwtDecode(data.access_token),
      profilePicture: googleDirectory.thumbnailPhotoUrl
    }));

    this.getPermission(client.applyPermissions).subscribe(res => {
      const castingPermissions = this.castingPermissions(res);
      this.store.dispatch(setPermissions({ permissions: castingPermissions }));
      this.redirectFromConsultantLogin();
    }, error => {
      this.notPermission();
    });
  }

  redirectFromConsultantLogin() {
    this.ngZone.run(() => this.router.navigate(['consultant/candidates'])).then();
  }

  notPermission() {
    this.store.dispatch(reset());
    this.ngZone.run(() => this.router.navigate(['consultant/login'])).then();
  }

  logOut(role) {
    this.store.dispatch(reset());
    localStorage.clear();
    if (role === 'consultant') {
      this.router.navigate(['consultant/login']);
      return false;
    }
  }

  // realizamos login contra randstad
  raasLogin(raasLoginData: RaasLogin) {
    const body = new URLSearchParams();
    body.set('client_id', AppConfig.settings.RAAS_CLIENT_ID);
    body.set('user_id', raasLoginData.userId);
    body.set('g_token', raasLoginData.gToken);
    body.set('grant_type', 'password');

    return this.httpClient.post<any>(AppConfig.settings.RAAS_BASE_URL + '/token', body.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    });
  }

  // realizamos login contra randstad utilizando las credenciales obtenidas de FBI
  crossStorageLogin(fbiData) {
    const crossStorageData = {
      apiToken: JSON.parse(fbiData[0]),
      googleAuth: JSON.parse(fbiData[1]),
      googleDirectory: JSON.parse(fbiData[2])
    };

    const rassLoginData = {
      gToken: crossStorageData.googleAuth.access_token,
      userId: crossStorageData.googleDirectory.emails[0].address,
      hostedDomain: crossStorageData.googleDirectory.emails[0].address.split('@')[1]
    };

    return this.httpClient.post<any>(`${AppConfig.settings.API}/consultant/login`, rassLoginData);
  }

  // Obtenemos los permisos del consultor
  getPermission(applyPermissions = true): Observable<OriginalPermission> {
    if (applyPermissions) {
      // ACCESS_PERMISSION es el campo resourceId, debemos preguntar por el "padre" que contiene todos los recursos
      return this.httpClient.get<any>(`${AppConfig.settings.PERMISSIONS_MANAGER}/users/permissions?resourceId=${AppConfig.settings.ACCESS_PERMISSION}`);
    } else {
      return of(AppConfig.settings.ALL_PERMISSIONS);
    }
  }

  // Transformamos los datos
  castingPermissions(res) {
    // Declaramos variables que usaremos para hacer el cast
    const originalPermissions = res;
    let permissions = [];
    const nodesObj = [];
    // Transformamos para maneter la homogeneidad con RC
    res.ResourceNodes.forEach(node => {
      nodesObj.push({
        id: node.ResourceId,
        name: node.ResourceName,
        resources: [{
          actionTypeId: node?.ResourceActions[0]?.ActionTypeId,
          allowChleck: node?.ResourceActions[0]?.AllowChk,
          description: node?.ResourceActions[0]?.Description
        }]
      });
    });

    permissions = [{
      id: originalPermissions?.ResourceId,
      name: originalPermissions?.ResourceName,
      resources: originalPermissions?.ResourceActions,
      nodes: nodesObj
    }];
    return permissions;
  }
}
